/**
 * Masters Page
 * Displays all masters with rating, status, commission debt
 * Allows blocking/unblocking masters
 * Requirements: 16.4, 16.5
 */

import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Users, 
  Search, 
  RefreshCw, 
  AlertCircle,
  Loader2,
  Star,
  Phone,
  Ban,
  CheckCircle,
  Clock,
  XCircle,
  Wifi,
  WifiOff,
  CreditCard,
  MapPin,
  Wrench,
} from 'lucide-react';
import { useAllMasters, useBlockMaster, useUnblockMaster } from '@/hooks/useMasters';
import { useToast } from '@/components/ui/toaster';
import type { Master, MasterStatus } from '@/lib/api';

type StatusFilter = 'all' | MasterStatus;

interface StatusConfig {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  variant: 'default' | 'secondary' | 'destructive' | 'outline' | 'success';
}

const STATUS_CONFIG: Record<MasterStatus, StatusConfig> = {
  PENDING_REVIEW: { label: 'На проверке', icon: Clock, variant: 'secondary' },
  APPROVED: { label: 'Одобрен', icon: CheckCircle, variant: 'success' },
  REJECTED: { label: 'Отклонён', icon: XCircle, variant: 'destructive' },
  ONLINE: { label: 'Онлайн', icon: Wifi, variant: 'success' },
  OFFLINE: { label: 'Оффлайн', icon: WifiOff, variant: 'outline' },
  BANNED: { label: 'Заблокирован', icon: Ban, variant: 'destructive' },
};

/**
 * Format commission debt for display
 */
function formatDebt(debt: string | null): string {
  if (!debt || debt === '0') return '0 ₽';
  const num = parseFloat(debt);
  return `${num.toLocaleString('ru-RU')} ₽`;
}

/**
 * Format rating with stars
 */
function formatRating(rating: number): string {
  return rating.toFixed(1);
}

/**
 * Master list item component
 */
function MasterListItem({
  master,
  onBlock,
  onUnblock,
  isBlocking,
  isUnblocking,
}: {
  master: Master;
  onBlock: (masterId: string) => void;
  onUnblock: (masterId: string) => void;
  isBlocking: boolean;
  isUnblocking: boolean;
}) {
  const statusConfig = STATUS_CONFIG[master.masterStatus];
  const StatusIcon = statusConfig.icon;
  const hasDebt = master.commissionDebt && parseFloat(master.commissionDebt) > 0;
  const isLoading = isBlocking || isUnblocking;

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          {/* Master Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-medium truncate">{master.name || 'Без имени'}</h3>
              <Badge variant={statusConfig.variant} className="shrink-0">
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusConfig.label}
              </Badge>
            </div>
            
            {/* Phone */}
            <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
              <Phone className="h-3 w-3" />
              <span>{master.phone || 'Не указан'}</span>
            </div>

            {/* Rating & Debt Row */}
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                <span className="font-medium">{formatRating(master.rating)}</span>
              </div>
              
              <div className={`flex items-center gap-1 ${hasDebt ? 'text-destructive' : 'text-muted-foreground'}`}>
                <CreditCard className="h-4 w-4" />
                <span className={hasDebt ? 'font-medium' : ''}>
                  {formatDebt(master.commissionDebt)}
                </span>
              </div>
            </div>

            {/* Districts */}
            {master.districts.length > 0 && (
              <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                <MapPin className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  {master.districts.slice(0, 2).join(', ')}
                  {master.districts.length > 2 && ` +${master.districts.length - 2}`}
                </span>
              </div>
            )}

            {/* Brands */}
            {master.brands.length > 0 && (
              <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                <Wrench className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  {master.brands.slice(0, 3).join(', ')}
                  {master.brands.length > 3 && ` +${master.brands.length - 3}`}
                </span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="shrink-0">
            {master.masterStatus === 'BANNED' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onUnblock(master.id)}
                disabled={isLoading}
              >
                {isUnblocking ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Разблокировать
                  </>
                )}
              </Button>
            ) : (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onBlock(master.id)}
                disabled={isLoading || master.masterStatus === 'PENDING_REVIEW' || master.masterStatus === 'REJECTED'}
              >
                {isBlocking ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Ban className="h-4 w-4 mr-1" />
                    Заблокировать
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function MastersPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [selectedMasterId, setSelectedMasterId] = useState<string | null>(null);
  const [blockReason, setBlockReason] = useState('');
  
  const { toast } = useToast();

  // Fetch all masters
  const { 
    data, 
    isLoading, 
    isError, 
    error, 
    refetch,
    isFetching,
  } = useAllMasters({
    limit: 100,
  });

  // Mutations
  const blockMutation = useBlockMaster();
  const unblockMutation = useUnblockMaster();

  // Filter masters
  const filteredMasters = useMemo(() => {
    if (!data?.data) return [];
    
    let result = data.data;

    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter(m => m.masterStatus === statusFilter);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(m => 
        m.name?.toLowerCase().includes(query) ||
        m.phone?.includes(query) ||
        m.telegramId.includes(query)
      );
    }

    return result;
  }, [data?.data, statusFilter, searchQuery]);

  // Stats
  const stats = useMemo(() => {
    if (!data?.data) return { total: 0, online: 0, blocked: 0, withDebt: 0 };
    
    const masters = data.data;
    return {
      total: masters.length,
      online: masters.filter(m => m.masterStatus === 'ONLINE').length,
      blocked: masters.filter(m => m.masterStatus === 'BANNED').length,
      withDebt: masters.filter(m => m.commissionDebt && parseFloat(m.commissionDebt) > 0).length,
    };
  }, [data?.data]);

  // Handlers
  const handleBlockClick = (masterId: string) => {
    setSelectedMasterId(masterId);
    setBlockReason('');
    setBlockDialogOpen(true);
  };

  const handleBlockConfirm = async () => {
    if (!selectedMasterId || blockReason.trim().length < 10) return;

    try {
      await blockMutation.mutateAsync({
        masterId: selectedMasterId,
        reason: blockReason.trim()
      });
      toast({
        title: 'Мастер заблокирован',
        description: 'Статус мастера успешно обновлён',
      });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      setBlockDialogOpen(false);
      setSelectedMasterId(null);
      setBlockReason('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
      console.error('[BlockMaster] Error:', err);
      toast({
        title: 'Ошибка блокировки',
        description: `${errorMessage} (ID: ${selectedMasterId})`,
        variant: 'destructive',
      });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
    }
  };

  const handleUnblock = async (masterId: string) => {
    try {
      await unblockMutation.mutateAsync(masterId);
      toast({
        title: 'Мастер разблокирован',
        description: 'Статус мастера успешно обновлён',
      });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
      console.error('[UnblockMaster] Error:', err);
      toast({
        title: 'Ошибка разблокировки',
        description: `${errorMessage} (ID: ${masterId})`,
        variant: 'destructive',
      });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Мастера</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2">
        <Card className="p-2 text-center">
          <div className="text-lg font-bold">{stats.total}</div>
          <div className="text-xs text-muted-foreground">Всего</div>
        </Card>
        <Card className="p-2 text-center">
          <div className="text-lg font-bold text-green-500">{stats.online}</div>
          <div className="text-xs text-muted-foreground">Онлайн</div>
        </Card>
        <Card className="p-2 text-center">
          <div className="text-lg font-bold text-red-500">{stats.blocked}</div>
          <div className="text-xs text-muted-foreground">Заблок.</div>
        </Card>
        <Card className="p-2 text-center">
          <div className="text-lg font-bold text-orange-500">{stats.withDebt}</div>
          <div className="text-xs text-muted-foreground">С долгом</div>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input 
            placeholder="Поиск по имени или телефону..." 
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Статус" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все статусы</SelectItem>
            <SelectItem value="ONLINE">Онлайн</SelectItem>
            <SelectItem value="OFFLINE">Оффлайн</SelectItem>
            <SelectItem value="APPROVED">Одобрен</SelectItem>
            <SelectItem value="BANNED">Заблокирован</SelectItem>
            <SelectItem value="PENDING_REVIEW">На проверке</SelectItem>
            <SelectItem value="REJECTED">Отклонён</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : isError ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-2" />
            <p className="text-destructive font-medium">Ошибка загрузки</p>
            <p className="text-sm text-muted-foreground mb-4">
              {error?.message || 'Не удалось загрузить данные'}
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Повторить
            </Button>
          </CardContent>
        </Card>
      ) : filteredMasters.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-muted-foreground" />
              Список мастеров
            </CardTitle>
            <CardDescription>
              Все зарегистрированные мастера
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
              <Users className="mb-2 h-12 w-12 opacity-50" />
              <p>Мастера не найдены</p>
              <p className="text-sm">
                {searchQuery || statusFilter !== 'all' 
                  ? 'Попробуйте изменить фильтры' 
                  : 'Зарегистрированные мастера появятся здесь'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* Results count */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Найдено: {filteredMasters.length}</span>
            {isFetching && (
              <span className="flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Обновление...
              </span>
            )}
          </div>

          {/* Master List */}
          {filteredMasters.map((master) => (
            <MasterListItem
              key={master.id}
              master={master}
              onBlock={handleBlockClick}
              onUnblock={handleUnblock}
              isBlocking={
                blockMutation.isPending && 
                blockMutation.variables?.masterId === master.id
              }
              isUnblocking={
                unblockMutation.isPending && 
                unblockMutation.variables === master.id
              }
            />
          ))}
        </div>
      )}

      {/* Block Dialog */}
      <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Заблокировать мастера</DialogTitle>
            <DialogDescription>
              Укажите причину блокировки. Мастер не сможет получать заказы до разблокировки.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Причина блокировки (минимум 10 символов)..."
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            className="min-h-[100px]"
          />
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setBlockDialogOpen(false)}
            >
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={handleBlockConfirm}
              disabled={blockReason.trim().length < 10 || blockMutation.isPending}
            >
              {blockMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Ban className="h-4 w-4 mr-2" />
              )}
              Заблокировать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
